/**
 * Organization Settings Page (Inline)
 *
 * Full-page replacement for OrganizationSettingsModal.
 * Works within the app.tsx tab navigation system (no Next.js router dependency).
 */

import React, { useState } from 'react';
import {
  ArrowLeft, Settings, Palette, MessageSquare, Video, Link2,
  Phone, QrCode, Shield, Building, Briefcase, CreditCard, Mail, Sparkles, Inbox, BellRing, TrendingUp,
} from 'lucide-react';
import { InvoicePreviewModal } from '../billing/InvoicePreviewModal';
import { BrandingSettings } from '../settings/branding';
import FormDesignTemplateManager from '../settings/forms/FormDesignTemplateManager';
import { CommunicationProviderSettings } from '../settings/CommunicationProviderSettings';
import { MeetingProviderSettings } from '../settings/MeetingProviderSettings';
import { ProductivityIntegrations } from '../settings/ProductivityIntegrations';
import { WorkspaceMessagingIntegrations } from '../settings/WorkspaceMessagingIntegrations';
import { PhoneNumberManagement } from '../settings/PhoneNumberManagement';
import { useTabbedChrome } from '@/components/weavein';
import { QRCodeManagement } from '../settings/QRCodeManagement';
import TransactionFeesCard from '@/src/platform/modules/billing/components/TransactionFeesCard';
import { OrgSecuritySettings } from '../settings/OrgSecuritySettings';
import { SupportAccessOrgSetting } from '@/components/impersonation/SupportAccessOrgSetting';
import { EmployerSetupSettings } from '../settings/EmployerSetupSettings';
import { EmployerCapabilitiesTable } from './EmployerCapabilitiesTable';
import { ReturnAddressSettings } from '../settings/ReturnAddressSettings';
import ContentComplianceSettings from '../settings/ContentComplianceSettings';
import CopilotProcedureReview from '../settings/CopilotProcedureReview';
import { config as productConfig } from '@/src/config';
// TRUS-709: member conversation visibility + staff names shown to members.
import { MemberInboxSettingsPanel, useMessagingSettings } from '../messaging/inbox/MemberInboxSettingsPanel';
// TRUS-745: DM Management moved here from the Inbox header — assigning member
// conversations is setup / caseload work, next to the visibility setting.
import { DMChannelManagement } from '../messaging/DMChannelManagement';
// TRUS-719: the org switch for ECM reminders by text / email to members.
import { EcmReminderSettingsPanel, useEcmReminderSettings } from './EcmReminderSettingsPanel';
import { useAppTranslation } from '@/contexts/TranslationContext';
// TRUS-776: org-scoped opportunity finder + funder outreach, behind its capability.
import FundingGrowthSettings from '../settings/funding/FundingGrowthSettings';
import { useCapability } from '@/hooks/useCapabilities';

type TabId = 'branding' | 'form_design' | 'providers' | 'meetings' | 'productivity' | 'messaging' | 'member_inbox' | 'ecm_reminders' | 'copilot_procedures' | 'phones' | 'qrcodes' | 'security' | 'compliance' | 'employer_setup' | 'employer' | 'billing' | 'mail' | 'funding_growth';

interface TabDef {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

// Reuse the OrgBillingTab from the modal (it's exported as part of the module)
// We'll import the billing tab inline since it's defined inside the modal file

interface Props {
  organizationId: number;
  organizationName: string;
  onBack: () => void;
  initialTab?: string;
}

export default function OrganizationSettingsPage({ organizationId, organizationName, onBack, initialTab }: Props) {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const isTriveny = productConfig.isTriveny;
  // Triveny tenants don't see the healthcare-specific billing/payer tabs,
  // so default to a tab that's actually available to them. The 'branding'
  // tab is the safe universal default.
  const [activeTab, setActiveTab] = useState<TabId>(
    (initialTab as TabId) || (isTriveny ? 'branding' : 'billing'),
  );

  // Healthcare-only tabs that Triveny doesn't need:
  //   • billing        — InlineBillingTab is the RevOps plan-tier admin
  //                      UI (Trust NPI / payer billing model). Triveny
  //                      uses platform-invoices + platform-revops for its
  //                      own billing config (Stripe Connect + invoice
  //                      templates), not direct-to-payer claims billing.
  //   • employer_setup — Healthcare employer benefits config (Medi-Cal
  //                      employer programs, ECM eligibility, etc.).
  //   • employer       — Healthcare employer programs (sponsor billing,
  //                      benefits enrollment).
  //   • compliance     — Content compliance for HIPAA / PHI handling.
  const HEALTHCARE_ONLY_TABS: TabId[] = [
    'billing',
    'employer_setup',
    'employer',
    'compliance',
    // Physical mail / print-and-mail letters (return address verification) is a
    // WeaveIn healthcare feature; the /api/letters/* endpoints aren't on ms-api
    // (404 on Triveny). Hide the tab for Triveny.
    'mail',
    // ECM (CalAIM Enhanced Care Management) reminders are a WeaveIn healthcare feature.
    'ecm_reminders',
    // Funding & Growth matches Medicaid/Medicare payers and public programs (TRUS-776).
    'funding_growth',
  ];

  // The Member Inbox tab shows once the settings endpoint answers (hidden on a
  // backend without it).
  const { data: messagingSettings } = useMessagingSettings();
  // The ECM reminders tab shows once its endpoint answers (hidden on a 404 / 501).
  const { data: ecmReminderSettings } = useEcmReminderSettings(!isTriveny);
  // Funding & Growth shows only for orgs with the capability on (TRUS-776).
  const { enabled: fundingGrowthEnabled } = useCapability(organizationId, 'funding_opportunities');
  const allTabs: TabDef[] = [
    { id: 'branding', label: 'Branding', icon: <Palette className="h-4 w-4" /> },
    { id: 'form_design', label: 'Form Design', icon: <Palette className="h-4 w-4" /> },
    { id: 'providers', label: 'Communications', icon: <MessageSquare className="h-4 w-4" /> },
    { id: 'meetings', label: 'Meeting Providers', icon: <Video className="h-4 w-4" /> },
    { id: 'productivity', label: 'Integrations', icon: <Link2 className="h-4 w-4" /> },
    { id: 'messaging', label: 'Messaging (Slack/Teams)', icon: <MessageSquare className="h-4 w-4" /> },
    ...(messagingSettings ? [{ id: 'member_inbox' as TabId, label: 'Member Inbox', icon: <Inbox className="h-4 w-4" /> }] : []),
    ...(ecmReminderSettings
      ? [{ id: 'ecm_reminders' as TabId, label: t('admin.ecmReminders.tab', 'ECM Reminders'), icon: <BellRing className="h-4 w-4" /> }]
      : []),
    { id: 'phones', label: 'Phone Numbers', icon: <Phone className="h-4 w-4" /> },
    { id: 'qrcodes', label: 'QR Codes', icon: <QrCode className="h-4 w-4" /> },
    { id: 'security', label: 'Security', icon: <Shield className="h-4 w-4" /> },
    { id: 'compliance', label: 'Compliance', icon: <Shield className="h-4 w-4" /> },
    // What the Copilot has learned from completed plans. Here rather than in
    // the panel: it is an org-wide decision about how everyone is told to work,
    // which is a settings question, not an assistant one.
    { id: 'copilot_procedures', label: 'Copilot', icon: <Sparkles className="h-4 w-4" /> },
    { id: 'employer_setup', label: 'Employer Setup', icon: <Building className="h-4 w-4" /> },
    { id: 'employer', label: 'Employer Programs', icon: <Briefcase className="h-4 w-4" /> },
    { id: 'mail', label: 'Mail / Letters', icon: <Mail className="h-4 w-4" /> },
    { id: 'billing', label: 'Billing', icon: <CreditCard className="h-4 w-4" /> },
    ...(fundingGrowthEnabled
      ? [{ id: 'funding_growth' as TabId, label: t('admin.funding_growth_tab', 'Funding & Growth'), icon: <TrendingUp className="h-4 w-4" /> }]
      : []),
  ];

  const tabs: TabDef[] = isTriveny
    ? allTabs.filter((t) => !HEALTHCARE_ONLY_TABS.includes(t.id))
    : allTabs;

  // If a deep-link initialTab landed us on a hidden tab (e.g. someone
  // bookmarked the WeaveIn billing URL), redirect to a safe tab so the
  // page doesn't render a stub.
  React.useEffect(() => {
    if (isTriveny && HEALTHCARE_ONLY_TABS.includes(activeTab)) {
      setActiveTab('branding');
    }
  }, [isTriveny, activeTab]);

  // Opening a row goes to that member's conversation in the Inbox (app.tsx
  // listens for app-open-comms-hub, parks the member and switches tab).
  const openMemberConversation = (_channelId: number, memberId?: number | null) => {
    if (memberId == null) return;
    window.dispatchEvent(new CustomEvent('app-open-comms-hub', { detail: { member_id: memberId }, cancelable: true }));
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'branding':
        return <BrandingSettings organizationId={organizationId} organizationName={organizationName} />;
      case 'form_design':
        return <FormDesignTemplateManager />;
      case 'providers':
        return <CommunicationProviderSettings organizationId={organizationId} initialSettings={undefined} onSettingsChange={() => {}} isSetupMode={false} />;
      case 'meetings':
        return <MeetingProviderSettings organizationId={organizationId} onSettingsChange={() => {}} />;
      case 'productivity':
        return <ProductivityIntegrations organizationId={organizationId} onSettingsChange={() => {}} />;
      case 'messaging':
        return <WorkspaceMessagingIntegrations organizationId={organizationId} onSettingsChange={() => {}} />;
      case 'member_inbox':
        return (
          <div className="space-y-8">
            <MemberInboxSettingsPanel organizationId={organizationId} />
            <section data-testid="org-settings-dm-assignments" className="rounded-lg border bg-white dark:border-slate-700 dark:bg-slate-900">
              <div className="border-b px-4 py-3 dark:border-slate-700">
                <h2 className="text-base font-semibold">{t('messaging.workspace.assignmentsTitle', 'Conversation assignments')}</h2>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'messaging.workspace.assignmentsHint',
                    'Filter member conversations, assign owners and reassign in bulk. Open a conversation to read it in the Inbox.',
                  )}
                </p>
              </div>
              <DMChannelManagement onSelectChannel={openMemberConversation} />
            </section>
          </div>
        );
      case 'ecm_reminders':
        return <EcmReminderSettingsPanel organizationId={organizationId} />;
      case 'phones':
        return <PhoneNumberManagement organizationId={organizationId} />;
      case 'qrcodes':
        return <QRCodeManagement organizationId={organizationId} />;
      case 'security':
        return (
          <div className="space-y-6">
            <OrgSecuritySettings organizationId={organizationId} onSettingsChange={() => {}} />
            {/* TRUS-747: the org's opt-out for support "View as user". */}
            <SupportAccessOrgSetting organizationId={organizationId} />
          </div>
        );
      case 'compliance':
        return <ContentComplianceSettings organizationId={organizationId} organizationName={organizationName} />;
      case 'copilot_procedures':
        // No organizationId prop: the endpoint scopes to the caller's own org
        // from their verified session, and passing one would invite a screen
        // that looks like it can review another org's.
        return <CopilotProcedureReview />;
      case 'employer_setup':
        return <EmployerSetupSettings organizationId={organizationId} organizationName={organizationName} onSetupComplete={() => {}} />;
      case 'employer':
        return <EmployerCapabilitiesTable organizationId={organizationId} organizationName={organizationName} />;
      case 'mail':
        return <ReturnAddressSettings organizationId={organizationId} />;
      case 'funding_growth':
        return fundingGrowthEnabled ? <FundingGrowthSettings organizationId={organizationId} /> : null;
      case 'billing':
        // Use the OrganizationSettingsModal's OrgBillingTab inline
        // We render it directly here since OrgBillingTab is not separately exported
        return <InlineBillingTab organizationId={organizationId} organizationName={organizationName} />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back button + header */}
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Back to Organizations
      </button>

      <div className="mb-6">
        {!tabbed && (
          <div className="flex items-center gap-3 mb-1">
            <Settings className="h-6 w-6 text-gray-600 dark:text-gray-300" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Organization Settings</h1>
          </div>
        )}
        <div className={`${tabbed ? '' : 'ml-9 '}flex items-center flex-wrap gap-2`}>
          <p className="text-sm text-gray-500 dark:text-gray-400">{organizationName}</p>
          {/* CBO ID — the unique code assigned to each organization (TRUS-312). */}
          <span
            className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500 dark:bg-gray-700 dark:text-gray-300"
            title="CBO ID (unique organization code)"
          >
            ID {organizationId}
          </span>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="flex gap-6">
        {/* Sidebar tabs */}
        <nav className="w-[220px] shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <ul className="py-1">
              {tabs.map(tab => (
                <li key={tab.id}>
                  <button
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-l-2 border-blue-600'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 border-l-2 border-transparent'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}


// Inline billing tab — READ-ONLY view of the org's plan tiers + effective
// transaction fees, via the weavein client-billing endpoint (works for org
// admins). Tier & fee CONFIGURATION is an operator function in RevOps; this
// view intentionally does not use the platform-only /api/platform/v1/revops
// endpoints (which an org admin's token cannot authenticate to).
function InlineBillingTab({ organizationId, organizationName }: { organizationId: number; organizationName: string }) {
  const [assignedTier, setAssignedTier] = React.useState<any | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { getBillingOverview } = await import('@/services/api/clientBillingAPI');
        // Scope to THIS org (not the viewer's own) so the assigned plan shows.
        const data: any = await getBillingOverview(organizationId);
        if (cancelled) return;
        // Only ever show the plan explicitly ASSIGNED to this org — never the
        // template catalog / a default fallback.
        setAssignedTier(data?.assigned_tier || null);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load billing');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [organizationId]);

  const pml = (m: string) => (m || 'per_seat').replace(/_/g, ' ');
  const tier = assignedTier;

  if (loading) return <div className="flex items-center justify-center py-12"><div className="h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" /><span className="text-sm text-gray-500">Loading billing...</span></div>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Billing &amp; Pricing</h3>
        <p className="text-sm text-gray-500">Plan and transaction fees assigned to <strong>{organizationName}</strong>.</p>
      </div>

      {error && <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error} <button onClick={() => setError(null)} className="ml-2 underline text-xs">dismiss</button></div>}

      {!tier ? (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-500">
          No plan has been assigned to this organization. Billing is configured by your platform operator.
        </div>
      ) : (
        <section>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assigned Plan</h4>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="border rounded-lg p-3 text-sm border-blue-300 bg-blue-50/50">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium">{tier.tier_name}</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400 capitalize">{pml(tier.pricing_model)}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">Current plan</span>
                </div>
              </div>
              <div className="text-gray-500 space-y-0.5">
                {tier.base_monthly_price > 0 && <div className="flex justify-between"><span>Base</span><span className="font-medium text-gray-700 dark:text-gray-300">${tier.base_monthly_price}/mo</span></div>}
                {tier.per_user_price > 0 && <div className="flex justify-between"><span>Per User</span><span className="font-medium text-gray-700 dark:text-gray-300">${tier.per_user_price}/user/mo</span></div>}
                {tier.setup_fee > 0 && <div className="flex justify-between"><span>Setup Fee</span><span className="font-medium text-gray-700 dark:text-gray-300">${tier.setup_fee}</span></div>}
                {tier.revenue_share_config?.reimbursement_share_percent > 0 && <div className="flex justify-between"><span>Revenue Share</span><span className="font-medium text-amber-600">{tier.revenue_share_config.reimbursement_share_percent}% reimb.</span></div>}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Self-hide when nothing is assigned to this org */}
      <TransactionFeesCard cboId={organizationId} />

      <InlineInvoicesSection organizationId={organizationId} />

      <InlinePaymentInfo organizationId={organizationId} />
    </div>
  );
}

/**
 * How this org can pay Trust Care — bank transfer/ACH + check details (and
 * whether online card payment is available). Read-only; org-scoped.
 */
function InlinePaymentInfo({ organizationId }: { organizationId: number }) {
  const [info, setInfo] = React.useState<any | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getPaymentInfo } = await import('@/services/api/clientBillingAPI');
        const data = await getPaymentInfo(organizationId);
        if (!cancelled) setInfo(data);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [organizationId]);

  if (!info) return null;
  const hasAny = info.ach?.available || info.check?.available || info.payment_instructions;
  if (!hasAny) return null;

  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">How to pay</h4>
      {info.ach?.available && (
        <div className="text-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">Bank transfer / ACH</p>
          <div className="text-gray-700 dark:text-gray-300 space-y-0.5">
            {info.ach.bank_name && <div>Bank: <span className="font-medium">{info.ach.bank_name}</span></div>}
            {info.ach.account_number && <div>Account #: <span className="font-medium">{info.ach.account_number}</span></div>}
            {info.ach.routing_number && <div>Routing #: <span className="font-medium">{info.ach.routing_number}</span></div>}
          </div>
        </div>
      )}
      {info.check?.available && (
        <div className="text-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">Check</p>
          <div className="text-gray-700 dark:text-gray-300 space-y-0.5">
            {info.check.payable_to && <div>Payable to: <span className="font-medium">{info.check.payable_to}</span></div>}
            {info.check.address && <div className="whitespace-pre-line">Mail to: {info.check.address}</div>}
          </div>
        </div>
      )}
      {info.payment_instructions && (
        <p className="text-sm text-gray-500 whitespace-pre-line">{info.payment_instructions}</p>
      )}
      {!info.card_setup_available && (
        <p className="text-xs text-gray-400">Online card payment isn’t enabled yet — pay by ACH/check or the payment link on each invoice.</p>
      )}
    </section>
  );
}

/**
 * Read-only list of the organization's own invoices from the unified
 * `org_invoices` system — the same invoices surfaced in Client Billing &
 * Payments. Lets an org admin see invoices issued to their org right here in
 * Org Settings → Billing.
 */
function InlineInvoicesSection({ organizationId }: { organizationId: number }) {
  const [invoices, setInvoices] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [previewId, setPreviewId] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { getInvoices } = await import('@/services/api/clientBillingAPI');
        const data = await getInvoices({ page: 1, page_size: 10, cboId: organizationId });
        if (!cancelled) setInvoices(data?.invoices || []);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load invoices');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [organizationId]);

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
  const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString('en-US') : '—');

  return (
    <section>
      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Invoices</h4>
      {error && <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 mb-2">{error}</div>}
      {loading ? (
        <p className="text-sm text-gray-500">Loading invoices…</p>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-gray-500">No invoices yet.</p>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                <th className="px-3 py-2 font-medium">Invoice #</th>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Due</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium text-right">Total</th>
                <th className="px-3 py-2 font-medium text-right">Amount Due</th>
                <th className="px-3 py-2 font-medium text-right">Invoice</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{inv.invoice_number}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{fmtDate(inv.invoice_date)}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{fmtDate(inv.due_date)}</td>
                  <td className="px-3 py-2 capitalize text-gray-600 dark:text-gray-400">{inv.status}</td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{fmt(inv.total)}</td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{fmt(inv.amount_due)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setPreviewId(String(inv.id))}
                      className="text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-400 mt-2">Full invoice history and payment lives in Client Billing &amp; Payments.</p>
      {previewId && (
        <InvoicePreviewModal invoiceId={previewId} cboId={organizationId} onClose={() => setPreviewId(null)} />
      )}
    </section>
  );
}
